import React from 'react';
import Dropzone from 'react-dropzone';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { Loader } from 'semantic-ui-react';
import { Icon } from '@weezevent/nacre';

import { fileExtensionsToAccept } from './mimeTypes';

/**
 * Display a drop zone wich can detect type of files or display a Progress bar.
 * @returns HTML dropzone or progress bar
 */
export default function DropzoneInput({ noClick, isUploading, disabled, onFileSelected, acceptedFileExtensions, maxSize, ...baseProps }) {
    /**
     * Action on dropzone to get files accepted and rejected by their type
     * @param accepted
     * @param rejected
     */
    const onDrop = React.useCallback(
        (accepted, rejected, event) => {
            onFileSelected(accepted, rejected);
        },
        [onFileSelected]
    );

    const acceptedMimeTypes = fileExtensionsToAccept(acceptedFileExtensions);

    return (
        <Dropzone noClick={noClick} accept={acceptedMimeTypes} onDrop={onDrop} disabled={disabled} multiple={false} maxSize={maxSize}>
            {props => <DropzoneRender acceptedFileExtensions={acceptedFileExtensions} isUploading={isUploading} maxSize={maxSize} {...baseProps} {...props} />}
        </Dropzone>
    );
}

DropzoneInput.propTypes = {
    acceptedFileExtensions: PropTypes.arrayOf(PropTypes.string).isRequired,
    isUploading: PropTypes.bool,
    disabled: PropTypes.bool,
    onFileSelected: PropTypes.func.isRequired,
    maxSize: PropTypes.number
};
DropzoneInput.defaultProps = {
    isUploading: false,
    disabled: false,
    maxSize: 5242880
};

function getIcon(accept, reject) {
    if (reject) {
        return 'message-warning';
    } else if (accept) {
        return 'image-upload';
    }
}

function DropzoneRender({
    isDragAccept,
    isDragReject,
    isDragActive,
    getRootProps,
    getInputProps,
    acceptedFileExtensions,
    icon,
    textRejected,
    label,
    textInfo,
    isUploading,
    maxSize
}) {
    const { t } = useTranslation();
    const className = `wz-dropzone ${isDragReject ? 'wz-dropzone__rejected' : ''} ${isDragAccept ? 'wz-dropzone__accepted' : ''}`;

    const _label = React.useMemo(() => {
        if (isDragReject) {
            return textRejected || t(['nacre.UploadButton.text-rejected', 'File type not accepted']);
        }

        return label;
    }, [label, isDragReject]);

    const _icon = getIcon(isDragAccept, isDragReject) || icon;

    if (isUploading) {
        return (
            <div className={className}>
                <Loader style={{ zIndex: '3' }} inline active>
                    <span style={{ fontSize: '12px' }}>{t('nacre.UploadButton.sendingFile')}</span>
                </Loader>
            </div>
        );
    }

    return (
        <div className={className} {...getRootProps()}>
            <input {...getInputProps()} />
            <div>
                {icon && (
                    <div className="wz-dropzone__icon">
                        <Icon size="huge" name={_icon} />
                    </div>
                )}
                <div className="wz-dropzone__label">{_label}</div>
            </div>
            {textInfo && <div className="wz-dropzone__text-info">{textInfo}</div>}
            {acceptedFileExtensions && acceptedFileExtensions.length > 0 && (
                <div className="wz-dropzone__type-file">
                    <Trans
                        i18nKey="nacre.UploadButton.text-allowed-file-extensions"
                        count={acceptedFileExtensions.length}
                        values={{
                            type: acceptedFileExtensions.join(', ')
                        }}
                    >
                        Only the file type (%type%) is allowed.
                    </Trans>
                </div>
            )}
            <span>{t(['nacre.UploadButton.text-file-max-weight'], { size: Math.trunc(maxSize / 1000000) })}</span>
        </div>
    );
}

DropzoneRender.propTypes = {
    icon: PropTypes.string,
    isDragReject: PropTypes.bool.isRequired,
    isDragAccept: PropTypes.bool.isRequired,
    isDragActive: PropTypes.bool,
    getRootProps: PropTypes.func.isRequired,
    getInputProps: PropTypes.func.isRequired,
    acceptedFileExtensions: PropTypes.arrayOf(PropTypes.string).isRequired,
    textRejectedMultipleFile: PropTypes.node,
    textRejected: PropTypes.node,
    textAccepted: PropTypes.node,
    label: PropTypes.node,
    textInfo: PropTypes.node,
    isUploading: PropTypes.bool
};
DropzoneRender.defaultProps = {
    isDragActive: false,
    icon: 'upload',
    textInfo: null,
    isUploading: false,
    label: null
};
